import { useState } from "react";

type Shown = { current: string; leaving: string | null; turn: number };

/**
 * A line of status text that changes without jumping: the old words lift away as the new ones
 * rise into the same place, and the line keeps the width of the longer one while both are there.
 * `from` is what was on screen before this mounted, when another element said it, so a handover
 * still reads as one line changing rather than a new line appearing.
 */
export function SwapText({ text, from }: { text: string; from?: string | null | undefined }) {
  const [shown, setShown] = useState<Shown>(() => ({
    current: from ?? text,
    leaving: null,
    turn: 0,
  }));
  // Adjusting state while rendering, React's pattern for state derived from a changed prop: the
  // swap starts in the same paint as the new text, with no frame of the old text alone.
  if (shown.current !== text) {
    setShown({ current: text, leaving: shown.current, turn: shown.turn + 1 });
  }
  const { current, leaving, turn } = shown;
  return (
    <span className="swap-text">
      {leaving !== null && (
        <span
          key={`leaving-${turn}`}
          className="swap-text-out"
          aria-hidden="true"
          onAnimationEnd={() =>
            setShown((now) => (now.turn === turn ? { ...now, leaving: null } : now))
          }
        >
          {leaving}
        </span>
      )}
      <span key={`current-${turn}`} className={turn > 0 ? "swap-text-in" : undefined}>
        {current}
      </span>
    </span>
  );
}
